import React, { PropsWithChildren, useEffect, useRef } from 'react';
import { PanResponder } from 'react-native';
import moment from 'moment';
import constants from '@/config/constants';
import { localLogoutUser } from '@/api/services/user';
import { getCurrentRoute } from '@/navigations/RootNavigation';
import {
  clearLocalData,
  getAccessToken,
  serverLogoutUser,
} from '@/stores/auth/services';
import store from '@/stores/store';
import utils from '@/utils/utils';
import { ScreenshotPrevention } from '@/components/ScreenshotPrevention';
import { SafeAreaProvider, SafeAreaView } from 'react-native-safe-area-context';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';

// Set max Idle time after that time tracking stops 1 min
const maxIdleTimeToEndSession = 60 * 1000; // 1 minute

export function Layout({ children }: PropsWithChildren) {
  const { showNotification } = useNotification();
  const timerForEndUserSessionId = useRef<number | undefined>(undefined);
  // watcher for when user logs in or out
  useEffect(() => {
    if (!store.getState().isAuthenticated) {
      clearTimeout(timerForEndUserSessionId.current);
    }
  }, [store.getState().isAuthenticated]);

  const checkIdleDateStampAndResetOrLogout = async () => {
    const { lastIdleDate } = store.getState();
    const logoutTime = moment(lastIdleDate).add(
      constants.idleTimeToLogout,
      'minutes',
    );
    const shouldLogout = logoutTime.isBefore(moment());

    if (lastIdleDate && shouldLogout) {
      showNotification({
        title: 'Sitzung abgelaufen. Bitte melden Sie sich neu an.',
        type: 'error',
      });
      await serverLogoutUser({ jwt: (await getAccessToken()) ?? '' });
      await clearLocalData();
      localLogoutUser();
      store.getActions().setLastIdleDate(null);
      return;
    }

    store.getActions().setLastIdleDate(moment().toDate());
  };

  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponderCapture: () => {
        const { resultModalIsShowing, stayLoggedIn, isAuthenticated } =
          store.getState();
        // start new session when user is back from idle
        if (
          getCurrentRoute()?.name === 'ExerciseProcess' &&
          !resultModalIsShowing
        ) {
          utils.startPracticeSessionTime();
        }

        // Track users who signed in without checkbox to stay logged in to log them out
        if (isAuthenticated && !stayLoggedIn) {
          checkIdleDateStampAndResetOrLogout();
        }

        // Track if user is Idle during their exercise to end the session
        if (getCurrentRoute()?.name === 'ExerciseProcess') {
          resetEndPracticeSessionAfterIdleTime();
        }
      },
    }),
  ).current;

  const resetEndPracticeSessionAfterIdleTime = () => {
    clearTimeout(timerForEndUserSessionId.current);

    timerForEndUserSessionId.current = setTimeout(async () => {
      if (store.getState().isAuthenticated && !store.getState().stayLoggedIn) {
        await serverLogoutUser({ jwt: (await getAccessToken()) as string });
        await localLogoutUser();
        await clearLocalData();
      }
      if (getCurrentRoute()?.name === 'ExerciseProcess') {
        utils.pushPracticeSessionTrackAndReset();
      }
    }, maxIdleTimeToEndSession);
  };

  return (
    <SafeAreaProvider>
      <SafeAreaView style={{ flex: 1 }} {...panResponder.panHandlers}>
        {constants.screenshotPreventionEnabled ? (
          <ScreenshotPrevention>{children}</ScreenshotPrevention>
        ) : (
          children
        )}
      </SafeAreaView>
    </SafeAreaProvider>
  );
}
